import { FRAGMENTS, getFragmentById } from '../data/fragments.data.js';

// Fragment runtime: behaves like a card stack.
//
// - drawPile is the active stack (top = end of array, drawn via pop()).
// - usedPile holds picked fragments and is reshuffled into the stack when the
//   draw pile cannot fulfill a hand.
// - drawHand() draws up to N cards, refilling from usedPile if needed.
// - resolveHand() applies the picked card to the player and returns the
//   unpicked cards to random positions in the remaining stack.

export class FragmentManager {
  constructor(rng) {
    this.rng = rng || Math.random;
    this.drawPile = [];
    this.usedPile = [];
    this.reset();
  }

  reset() {
    this.drawPile = FRAGMENTS.map((f) => f.id);
    this.usedPile = [];
    this._shuffle(this.drawPile);
  }

  // Fisher-Yates in place using the manager's RNG.
  _shuffle(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(this.rng() * (i + 1));
      const tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;
    }
    return arr;
  }

  _refillFromUsed() {
    if (this.usedPile.length === 0) return;
    const recycled = this.usedPile.splice(0, this.usedPile.length);
    this._shuffle(recycled);
    // Place recycled cards at the bottom of the draw pile.
    this.drawPile = recycled.concat(this.drawPile);
  }

  drawHand(count = 3) {
    if (this.drawPile.length < count) this._refillFromUsed();
    const out = [];
    const n = Math.min(count, this.drawPile.length);
    for (let i = 0; i < n; i++) {
      const id = this.drawPile.pop();
      const f = getFragmentById(id);
      if (f) out.push(f);
    }
    return out;
  }

  // Apply the picked fragment to the player and return unpicked cards back to
  // random positions in the remaining draw pile.
  resolveHand(hand, pickedId, player) {
    if (!hand || hand.length === 0) return null;
    let picked = null;
    const unpickedIds = [];
    for (const f of hand) {
      if (f.id === pickedId && !picked) picked = f;
      else unpickedIds.push(f.id);
    }
    if (picked) {
      this.usedPile.push(picked.id);
      player.applyFragment(picked.id);
    }
    for (const id of unpickedIds) {
      const idx = Math.floor(this.rng() * (this.drawPile.length + 1));
      this.drawPile.splice(idx, 0, id);
    }
    return picked;
  }

  // Backwards-compatible shims so older callers still work if any exist.
  offerChoices(count = 3) {
    return this.drawHand(count);
  }

  pick(id, player) {
    if (!this.usedPile.includes(id)) this.usedPile.push(id);
    player.applyFragment(id);
  }
}
